import React, { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs, { Dayjs } from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import styled from 'styled-components';
import { ButtonIcon, Icon } from 'components';
import { theme, toRem } from 'design';
import { useKeyPressed } from 'hooks';

dayjs.extend(weekday);

interface IDailyCalendarProps {
  dataCy?: string;
  expandedDay?: React.ReactNode;
  futureDayMax?: number;
  includeCheck?: boolean;
  isLoading?: boolean;
  onCalNavigation?: (day?: Dayjs) => void;
  onDayClick?: (day?: Dayjs) => void;
  previousDayMax?: number;
  selectedDay?: Dayjs;
}

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
  max-width: ${toRem(400)};
  margin: 0 auto;
  background-color: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  padding: ${toRem(40)};

  @media (max-width: ${400}px) {
    padding: ${toRem(40)} ${toRem(20)};
  }
`;

const Month = styled.h3`
  font-size: ${toRem(16)};
  font-weight: ${theme.font.weight.semiBold};
  text-transform: uppercase;
  padding-bottom: ${toRem(24)};
`;

const Week = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
`;

const Day = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  margin-right: ${toRem(8)};
  padding: ${toRem(14)} ${toRem(16)};
  width: ${toRem(80)};
  height: ${toRem(80)};

  &:hover,
  &:focus {
    background: ${theme.color.neutral[200]};
    cursor: pointer;
  }

  &:focus-visible {
    outline: none;
  }

  &:active,
  &.is-active {
    background-color: ${theme.color.secondary[400]};

    p {
      color: ${theme.color.white};
    }
  }
`;

const Weekday = styled.p`
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(12)};
  font-weight: ${theme.font.weight.semiBold};
  padding-bottom: ${toRem(8)};
  text-transform: uppercase;
`;

const Date = styled.p`
  color: ${theme.color.neutral[900]};
  font-size: ${toRem(32)};
  font-weight: ${theme.font.weight.regular};
`;

const StyledButtonIcon = styled(ButtonIcon)`
  border: none;

  &:first-child {
    margin-right: ${toRem(8)};
  }

  &:hover,
  &:focus {
    background: ${theme.color.neutral[200]};
    border-radius: ${toRem(4)};
  }

  &:disabled,
  &:disabled:hover {
    background: none;
    cursor: not-allowed;
  }
`;

const ExpandedDayInfo = styled.section`
  margin-top: ${toRem(24)};
  width: 100%;
`;

const DailyCalendar = ({ dataCy = 'daily-calendar', expandedDay, futureDayMax, includeCheck, isLoading, onCalNavigation, onDayClick, previousDayMax, selectedDay }: IDailyCalendarProps) => {
  const [dayCounter, setDayCounter] = useState<number>(0);
  const [visibleDay, setVisibleDay] = useState<Dayjs>(selectedDay || dayjs());
  const [activeDay, setActiveDay] = useState<Dayjs | null>(selectedDay || null);

  useEffect(() => {
    if (selectedDay && !activeDay) setActiveDay(selectedDay);

    if (selectedDay) {
      const today = dayjs();
      if (selectedDay.isBefore(today)) {
        const dayDiff = today.diff(selectedDay, 'day');
        setDayCounter(dayCounter + dayDiff);
        setVisibleDay(today.subtract(dayDiff, 'day'));
      }

      if (selectedDay.isAfter(today)) {
        const dayDiff = selectedDay.diff(today, 'day');
        setDayCounter(dayCounter - dayDiff);
        setVisibleDay(today.add(dayDiff, 'day'));
      }
    }
  }, []);

  useEffect(() => onCalNavigation?.(visibleDay), [visibleDay]);

  const onDaySelect = (day: Dayjs) => {
    setActiveDay(day);
    onDayClick?.(day);
  };

  const onPreviousWeekClick = () => {
    if (previousDayMax && dayCounter === previousDayMax) return;
    setDayCounter(dayCounter + 1);
    setVisibleDay(visibleDay.subtract(1, 'day'));
  };

  const onNextWeekClick = () => {
    setDayCounter(dayCounter - 1);
    setVisibleDay(visibleDay.add(1, 'day'));
  };

  const enterKeyPress = useKeyPressed((event: KeyboardEvent) => event.key === 'Enter');

  const displayWeek = useMemo(
    () =>
      isLoading ? (
        <Day>
          <Weekday>
            <Skeleton width={50} />
          </Weekday>
          <Skeleton width={30} />
        </Day>
      ) : (
        <>
          {includeCheck && <Icon icon="CheckmarkThick" fill={activeDay?.isSame(visibleDay, 'day') ? theme.color.white : theme.color.secondary[400]} style={{ position: 'relative', top: '-25px', left: '75px' }} />}
          <Day
            data-cy={`${dataCy}-day`}
            tabIndex={0}
            role="button"
            className={activeDay?.isSame(visibleDay, 'day') && 'is-active'}
            onClick={() => onDaySelect(visibleDay)}
            onKeyUp={() => {
              if (enterKeyPress) onDaySelect(visibleDay);
            }}
          >
            <Weekday>{visibleDay.format('ddd')}</Weekday>
            <Date>{visibleDay.format('D')}</Date>
          </Day>
        </>
      ),
    [visibleDay, activeDay, enterKeyPress, isLoading]
  );

  return (
    <Layout data-cy={dataCy}>
      <Month data-cy={`${dataCy}-month`}>{isLoading ? <Skeleton width={100} /> : visibleDay.format('MMMM YYYY')}</Month>
      <Week data-cy={`${dataCy}-week-view`} aria-label={`Calendar view for ${visibleDay.format('MMMM D YYYY')}`}>
        <StyledButtonIcon data-cy={`${dataCy}-previous-week-btn`} aria-label="Previous week" icon="Previous" type="button" appearance="white" onClick={onPreviousWeekClick} isLoading={isLoading} disabled={dayCounter >= previousDayMax} />
        {displayWeek}
        <StyledButtonIcon
          data-cy={`${dataCy}-next-week-btn`}
          aria-label="Next week"
          icon="Next"
          type="button"
          appearance="white"
          onClick={onNextWeekClick}
          isLoading={isLoading}
          disabled={(!futureDayMax && dayCounter <= -futureDayMax) || (futureDayMax === 0 && dayCounter === 0)}
        />
      </Week>
      {expandedDay && activeDay && <ExpandedDayInfo data-cy={`${dataCy}-expanded-day-info`}>{expandedDay}</ExpandedDayInfo>}
    </Layout>
  );
};

export default DailyCalendar;
